<template>
  <nav class="navbar" role="navigation" aria-label="Main navigation">
    <div class="navbar-left">
      <div class="navbar-logo__container">
        <button
          class="nav-btn nav-logo__container"
          @click="toggleDropdown"
          aria-haspopup="true"
          :aria-expanded="showDropdown.toString()"
        >
          <div class="square" :style="logoStyle" aria-hidden="true"></div>
          <Icon :class="{ 'rotated__icon': showDropdown }" name="vector-down" size="20" />
        </button>

        <div v-if="showDropdown" class="navbar-logo__dropdown" role="menu">
          <div class="navbar-logo__li" @click="openChangeLogoModal" role="menuitem" tabindex="0">
            <Icon name="edit" size="12" />
            Сменить
          </div>
        </div>
      </div>

      <button class="nav-btn" @click="goTo('admin')" aria-label="Admin">
        <Icon name="db" size="20" />
      </button>

      <button class="nav-btn" @click="openSuggestionsModal" aria-label="Suggestions">
        <Icon name="forms" size="20" />
      </button>

      <Multiselect
        v-model="currentSize"
        :options="screenSizes"
        label="title"
        placeholder="Размер"
        @update:modelValue="updateCurrentSize"
      />
    </div>

    <div class="navbar-right">
      <button class="nav-btn btn__load" @click="uploadLanding" title="Выгрузить лэндинг">
        <Icon name="box-upload" size="20" />
        <span class="btn-text">Выгрузить</span>
      </button>

      <button class="nav-btn btn__load" @click="confirmInitLanding" title="Загрузить лэндинг">
        <Icon name="sync" size="20" />
        <span class="btn-text">Загрузить</span>
      </button>

      <button class="nav-btn" @click="$emit('open-preview')" title="Открыть превью">
        <Icon name="play" size="20" />
      </button>

      <div class="profile-wrapper">
        <button class="nav-btn" @click="openProfileModal" aria-haspopup="dialog" title="Профиль">
          <Icon name="profile" size="20" />
        </button>

        <ProfileModal v-model="showProfileModal" @edit-profile="onEditProfile" />
      </div>
    </div>

    <!-- Modals -->
    <ChangeLogoModal
      v-model="showChangeLogoModal"
      :initialFiles="initialLogoFiles"
      @logo-changed="onLogoChanged"
    />

    <SuggestionsModal v-model="showSuggestionsModal" />

    <ConfirmModal
      v-model="showConfirm"
      title="Загрузить данные с сервера?"
      :message="confirmMessage"
      :allowHtml="true"
      confirmText="Загрузить"
      @confirm="onConfirmLoad"
      @cancel="onCancelLoad"
    />
  </nav>
</template>

<script>
import { mapGetters } from 'vuex'
import ProfileModal from '@/components/pages/admin/profile/ProfileModal.vue'
import Multiselect from '@/components/UI/Select/Multiselect.vue'
import ChangeLogoModal from '@/components/UI/Modal/ChangeLogoModal.vue'
import SuggestionsModal from '@/components/UI/Modal/SuggestionsModal.vue'
import ConfirmModal from '@/components/UI/Modal/ConfirmModal.vue'
import Icon from '@/components/UI/Icon/Icon.vue'
import { screenSizes } from '@/config/screen-sizes.js'

export default {
  name: 'PageNav',
  components: {
    ProfileModal,
    Multiselect,
    ChangeLogoModal,
    SuggestionsModal,
    ConfirmModal,
    Icon
  },
  emits: ['open-preview', 'open-profile', 'on-edit-profile', 'logo-updated'],
  data () {
    return {
      showDropdown: false,
      currentSize: null,
      screenSizes,
      showProfileModal: false,
      showChangeLogoModal: false,
      showSuggestionsModal: false,
      showConfirm: false,
      pendingInitUid: null
    }
  },
  computed: {
    ...mapGetters('landing', ['landingSettings', 'landingDataSynced']),

    logoUrl () {
      return this.landingSettings?.logo || null
    },

    logoStyle () {
      if (this.logoUrl) {
        return {
          backgroundImage: `url(${this.logoUrl})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }
      }
      return {
        backgroundColor: 'gray'
      }
    },

    initialLogoFiles () {
      return this.logoUrl ? [{ name: 'logo', url: this.logoUrl, type: 'image/svg+xml' }] : []
    },

    confirmMessage () {
      // Показываем предупреждение, если есть несинхронизированные изменения
      if (this.landingDataSynced === false) {
        return `<strong>Внимание:</strong> локальные изменения не сохранены. При загрузке с сервера они будут потеряны. Продолжить?`
      }
      return `Данные будут обновлены с сервера. Продолжить?`
    }
  },
  methods: {
    toggleDropdown () {
      this.showDropdown = !this.showDropdown
    },

    goTo (path) {
      this.$router.push('/' + path)
    },

    openChangeLogoModal () {
      this.showDropdown = false
      this.showChangeLogoModal = true
    },

    openSuggestionsModal () {
      this.showSuggestionsModal = true
    },

    updateCurrentSize (size) {
      const width = parseInt(size.title, 10) || window.innerWidth
      const height = window.innerHeight
      this.$store.dispatch('landing/UPDATE_LANDING_SETTINGS', {
        size: { width, height }
      })
    },

    openProfileModal () {
      this.showProfileModal = !this.showProfileModal
    },

    onLogoChanged (url) {
      const newSettings = {
        ...(this.landingSettings || {}),
        logo: url
      }
      this.$store.dispatch('landing/UPDATE_LANDING_SETTINGS', newSettings)
      this.showChangeLogoModal = false
      this.$emit('logo-updated', url)
    },

    onEditProfile () {
      this.$emit('on-edit-profile')
    },

    confirmInitLanding () {
      const uid = this.landingSettings?.uid ? this.landingSettings.uid : null
      if(!uid) {
        this.$store.dispatch('alert/showAlert', {
          title: 'Ошибка',
          message: 'Не удалось загрузить данные! Данные отсутсвуют',
          type: 'error',
          typeClass: 'alert-error',
          background: '#f8d7da',
          color: '#721c24',
          autoClose: { enabled: true, delay: 5000 }
        })
        return
      } 

      if (this.landingDataSynced === true) {
        this.doInitLanding(uid)
        return
      }

      this.pendingInitUid = uid
      this.showConfirm = true
    },


    // actual load from server
    async doInitLanding (uid) {
      try {
        await this.$store.dispatch('landing/initLandingSettings', { uid })
        // обновляем размер после загрузки
        const width = 1245
        const height = window.innerHeight
        this.$store.dispatch('landing/UPDATE_LANDING_SETTINGS', {
          size: { width, height }
        })
      } catch (err) {
        console.error('doInitLanding failed', err)
        this.$store.dispatch('alert/showAlert', {
          title: 'Ошибка',
          message: 'Не удалось загрузить данные!',
          type: 'error',
          typeClass: 'alert-error',
          background: '#f8d7da',
          color: '#721c24',
          autoClose: { enabled: true, delay: 5000 }
        })
      }
    },

    onConfirmLoad () {
      this.showConfirm = false
      const uid = this.pendingInitUid || (this.landingSettings && this.landingSettings.uid)
      this.pendingInitUid = null
      if (uid) this.doInitLanding(uid)
    },

    onCancelLoad () {
      this.showConfirm = false
      this.pendingInitUid = null
      this.$store.dispatch('alert/showAlert', {
        title: 'Отмена',
        message: 'Загрузка отменена, локальные изменения сохранены.',
        type: 'info',
        typeClass: 'alert-info',
        background: '#e6f2f0',
        color: '#0b5345',
        autoClose: { enabled: true, delay: 2500 }
      })
    },

    async uploadLanding () {
      try {
        await this.$store.dispatch('landing/uploadLandingSettingsToServer')

        const user = this.$store.getters['user/user'] || {}
        const updatedBy = user.uid || null
        await this.$store.dispatch('landing/uploadLandingDataToServer', {
          updated_by: updatedBy
        })

        
        const width = 1245
        const height = window.innerHeight
        this.$store.dispatch('landing/UPDATE_LANDING_SETTINGS', {
          size: { width, height }
        })

        this.$store.dispatch('alert/showAlert', {
          title: 'Успех',
          message: 'Лэндинг успешно выгружен!',
          type: 'success',
          typeClass: 'alert-success',
          background: '#d4edda',
          color: '#155724',
          autoClose: { enabled: true, delay: 3000 },
          position: { top: '50px', right: '50px', zIndex: 1100 }
        })
      } catch (err) {
        console.error('uploadLanding failed', err)
        this.$store.dispatch('alert/showAlert', {
          title: 'Ошибка',
          message: 'Не удалось сохранить данные!',
          type: 'error',
          typeClass: 'alert-error',
          background: '#f8d7da',
          color: '#721c24',
          autoClose: { enabled: true, delay: 5000 }
        })
      }
    }
  }
}
</script>


<style scoped>
.navbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--grey-2);
  padding: 0.5rem 1rem;
  gap: 0.7rem;
  position: fixed;
  width: 100%;
  z-index: 2;
  height: 3rem;
}

.navbar-left,
.navbar-right {
  display: flex;
  gap: 0.7rem;
}

.nav-btn {
  background: var(--color-bg);
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  height: 2rem;
  align-items: center;
  justify-content: center;
  color: gray;
  transition: color 0.3s ease, background 0.3s ease;
}

.nav-btn svg path,
.nav-btn svg circle,
.nav-btn svg rect,
.nav-btn svg polygon {
  fill: currentColor;
  stroke: currentColor;
}

.nav-btn .arrow {
  margin-left: 4px;
}

.nav-btn:hover {
  background: #3a3a3a;
  color: white;
}

.heroes__title {
  color: var(--blue-2);
  text-decoration: underline;
  display: flex;
}
.navbar-logo__container {
  position: relative;
}
.navbar-logo__dropdown {
  position: absolute;
  border-radius: 0 0 0.5rem 0.5rem;
  background: var(--grey-1);
  margin-top: -0.2rem;
  width: 100%;
  overflow: hidden;
}

.navbar-logo__li {
  padding: 0.5rem;
  display: flex;
  gap: 0.2rem;
  font-size: 0.7rem;
}
.navbar-logo__li:hover {
  background: #3a3a3a;
  color: white;
  cursor: pointer;
}
.rotated__icon {
  transform: rotate(180deg);
}
.nav-logo__container {
  gap: 0.5rem;
}
.square {
  width: 2rem;
  height: 1.2rem;
  background: gray;
  border-radius: 4px;
}
.btn__load {
  padding: 0.5rem;
  gap: 0.5rem;
}
</style>
